import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import { notifyFriendRequest, notifyFriendAccepted } from '../lib/notificationTriggers';
import { sendFriendRequestPush } from '../lib/onesignal';
import toast from 'react-hot-toast';
import {
  FaUsers,
  FaUserPlus,
  FaEnvelope,
  FaCheck,
  FaClock,
  FaTimes,
  FaSearch,
  FaArrowLeft,
  FaHeart,
  FaComments,
  FaUserFriends,
  FaSpinner
} from 'react-icons/fa';

interface User {
  id: string;
  email: string;
  profile_image: string | null;
  full_name: string | null;
  is_online: boolean;
  created_at: string;
  friendship_status?: 'none' | 'pending_sent' | 'pending_received' | 'accepted' | 'blocked';
  friendship_id?: string;
  is_featured?: boolean;
  username?: string | null;
}

interface FriendRequest {
  id: string;
  requester_id: string;
  addressee_id: string;
  status: 'pending' | 'accepted' | 'blocked';
  created_at: string;
  requester_email: string;
  requester_profile_image: string | null;
  requester_full_name: string | null;
}

export default function Learners() {
  const [users, setUsers] = useState<User[]>([]);
  const [displayedUsers, setDisplayedUsers] = useState<User[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRequests, setShowRequests] = useState(false);
  const [processingRequests, setProcessingRequests] = useState<Set<string>>(new Set());
  const [showMore, setShowMore] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const INITIAL_DISPLAY_COUNT = 10;

  // Redirect if no custom-auth user
  useEffect(() => {
    if (!currentUser?.id) {
      navigate('/login');
      return;
    }
    loadAll();
    // Realtime: refresh when friendships that involve me change
    const ch = supabase
      .channel(`learners-friendships-${currentUser.id}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'user_friendships', filter: `or(requester_id.eq.${currentUser.id},addressee_id.eq.${currentUser.id})` },
        () => {
          // Refresh both lists
          loadUsers();
          loadFriendRequests();
        }
      )
      .subscribe();

    return () => {
      try { supabase.removeChannel(ch); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  useEffect(() => {
    filterAndDisplayUsers();
  }, [users, searchQuery, showMore]);

  async function loadAll() {
    setLoading(true);
    try {
      await Promise.all([loadUsers(), loadFriendRequests()]);
    } catch (err: any) {
      console.error('Error loading learners page:', err);
      toast.error(humanizeError(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }

  // Load users (everyone except me) and annotate with friendship status
  async function loadUsers() {
    const uid = currentUser!.id;
    try {
      const { data: usersData, error: usersError } = await supabase
        .from('stripeusers')
        .select('id, email, profile_image, full_name, is_online, created_at, is_featured, username')
        .neq('id', uid)
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;

      // Fetch all friendships that involve me, then map per user
      const { data: friendships, error: frErr } = await supabase
        .from('user_friendships')
        .select('id, requester_id, addressee_id, status')
        .or(`requester_id.eq.${uid},addressee_id.eq.${uid}`);

      if (frErr) throw frErr;

      const usersWithStatus: User[] = (usersData || []).map((u) => {
        const f = friendships?.find(
          (x) => (x.requester_id === uid && x.addressee_id === u.id) || (x.addressee_id === uid && x.requester_id === u.id)
        );
        let status: User['friendship_status'] = 'none';
        if (f) {
          if (f.status === 'accepted') status = 'accepted';
          else if (f.status === 'blocked') status = 'blocked';
          else if (f.requester_id === uid) status = 'pending_sent';
          else status = 'pending_received';
        }
        return { ...u, friendship_status: status, friendship_id: f?.id };
      })
      .sort((a, b) => {
        // Featured users first
        if (a.is_featured && !b.is_featured) return -1;
        if (!a.is_featured && b.is_featured) return 1;
        // Then sort by creation date (newest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      setUsers(usersWithStatus);
    } catch (err: any) {
      console.error('Error loading users:', err);
      toast.error(humanizeError(err, 'Failed to load users'));
    }
  }

  // Load pending requests sent TO me (no cross-table join to avoid RLS surprises)
  async function loadFriendRequests() {
    const uid = currentUser!.id;
    try {
      const { data: reqs, error } = await supabase
        .from('user_friendships')
        .select('id, requester_id, addressee_id, status, created_at')
        .eq('addressee_id', uid)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!reqs || reqs.length === 0) {
        setFriendRequests([]);
        return;
      }

      // Fetch requester user records in one batch
      const requesterIds = Array.from(new Set(reqs.map((r) => r.requester_id)));
      const { data: requesters, error: usersErr } = await supabase
        .from('stripeusers')
        .select('id, email, profile_image, full_name')
        .in('id', requesterIds);

      if (usersErr) throw usersErr;

      const map = new Map(requesters?.map((u) => [u.id, u]));
      const formatted: FriendRequest[] = reqs
        .map((r) => {
          const ru = map.get(r.requester_id);
          if (!ru) return null;
          return {
            id: r.id,
            requester_id: r.requester_id,
            addressee_id: r.addressee_id,
            status: r.status as FriendRequest['status'],
            created_at: r.created_at,
            requester_email: ru.email,
            requester_profile_image: ru.profile_image ?? null,
            requester_full_name: ru.full_name ?? null
          } as FriendRequest;
        })
        .filter(Boolean) as FriendRequest[];

      setFriendRequests(formatted);
    } catch (err: any) {
      console.error('Error loading friend requests:', err);
      // Don’t toast spam if there are simply none; show only real errors
      if (err?.code !== 'PGRST116') {
        toast.error(humanizeError(err, 'Failed to load friend requests'));
      }
    }
  }

  function filterAndDisplayUsers() {
    const query = searchQuery.trim().toLowerCase();
    const filtered = query
      ? users.filter(
          (u) =>
            u.email.toLowerCase().includes(query) ||
            (u.full_name && u.full_name.toLowerCase().includes(query))
        )
      : users;

    const displayCount = showMore ? filtered.length : INITIAL_DISPLAY_COUNT;
    setDisplayedUsers(filtered.slice(0, displayCount));
  }

  async function sendFriendRequest(userId: string) {
    if (!currentUser?.id) return;
    try {
      setProcessingRequests((s) => new Set(s).add(userId));
      const { error } = await supabase
        .from('user_friendships')
        .insert({ requester_id: currentUser.id, addressee_id: userId, status: 'pending' });
      if (error) throw error;
      
      // Get addressee info for notification
      const { data: addressee } = await supabase
        .from('stripeusers')
        .select('email, full_name')
        .eq('id', userId)
        .single();

      if (addressee) {
        const requesterName = currentUser.full_name || currentUser.username || currentUser.email || 'Someone';
        // Create in-app notification
        notifyFriendRequest(userId, currentUser.id, requesterName).catch(console.error);
        // Send push notification directly from sender
        sendFriendRequestPush(userId, requesterName).catch(console.error);
      }
      
      toast.success('Friend request sent!');
      await loadUsers();
    } catch (err: any) {
      console.error('Error sending friend request:', err);
      toast.error(humanizeError(err, 'Failed to send friend request'));
    } finally {
      setProcessingRequests((s) => {
        const n = new Set(s);
        n.delete(userId);
        return n;
      });
    }
  }

  async function respondToFriendRequest(requestId: string, action: 'accept' | 'decline') {
    try {
      setProcessingRequests((s) => new Set(s).add(requestId));
      if (action === 'accept') {
        // Get the friendship to find the requester
        const { data: friendship, error: fetchErr } = await supabase
          .from('user_friendships')
          .select('requester_id, addressee_id')
          .eq('id', requestId)
          .single();
        if (fetchErr) throw fetchErr;

        const { error } = await supabase.from('user_friendships').update({ status: 'accepted' }).eq('id', requestId);
        if (error) throw error;

        // Notify the original requester that their request was accepted
        if (friendship && currentUser?.id) {
          const accepterName = currentUser.full_name || currentUser.username || currentUser.email || 'Someone';
          notifyFriendAccepted(friendship.requester_id, currentUser.id, accepterName).catch(
            (err) => console.error('[Push] Failed to send friend accepted push:', err)
          );
        }

        toast.success('Friend request accepted!');
      } else {
        const { error } = await supabase.from('user_friendships').delete().eq('id', requestId);
        if (error) throw error;
        toast.success('Friend request declined');
      }
      await Promise.all([loadFriendRequests(), loadUsers()]);
    } catch (err: any) {
      console.error('Error responding to friend request:', err);
      toast.error(humanizeError(err, 'Failed to respond to friend request'));
    } finally {
      setProcessingRequests((s) => {
        const n = new Set(s);
        n.delete(requestId);
        return n;
      });
    }
  }

  function handleMessage(userId: string) {
    navigate('/messages', { state: { friendId: userId } });
  }

  function getFriendshipButton(user: User) {
    const isProcessing = processingRequests.has(user.id);
    switch (user.friendship_status) {
      case 'none':
        return (
          <button
            onClick={() => sendFriendRequest(user.id)}
            disabled={isProcessing}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-black font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            {isProcessing ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaUserPlus className="w-4 h-4" />}
            <span>{isProcessing ? 'Sending...' : 'Add Friend'}</span>
          </button>
        );
      case 'pending_sent':
        return (
          <div className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl border border-gray-200">
            <FaClock className="w-4 h-4" />
            <span className="font-medium">Request Sent</span>
          </div>
        );
      case 'pending_received':
        return (
          <div className="w-full flex items-center space-x-2">
            <button
              onClick={() => respondToFriendRequest(user.friendship_id!, 'accept')}
              disabled={isProcessing}
              className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 rounded-lg text-white transition-colors duration-200 font-medium"
            >
              <FaCheck className="w-3 h-3" />
              <span>Accept</span>
            </button>
            <button
              onClick={() => respondToFriendRequest(user.friendship_id!, 'decline')}
              disabled={isProcessing}
              className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 rounded-lg text-white transition-colors duration-200 font-medium"
            >
              <FaTimes className="w-3 h-3" />
              <span>Decline</span>
            </button>
          </div>
        );
      case 'accepted':
        return (
          <button
            onClick={() => handleMessage(user.id)}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-xl text-white font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <FaComments className="w-4 h-4" />
            <span>Message</span>
          </button>
        );
      default:
        return null;
    }
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const handleSeeMore = () => setShowMore(true);
  const handleShowLess = () => setShowMore(false);

  // Derived counts
  const acceptedCount = useMemo(() => users.filter((u) => u.friendship_status === 'accepted').length, [users]);
  const featuredCount = useMemo(() => users.filter((u) => u.is_featured).length, [users]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-black via-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="flex items-center space-x-2 text-yellow-400 hover:text-yellow-300 transition-colors duration-200 group"
              >
                <div className="p-2 rounded-full bg-yellow-400/20 group-hover:bg-yellow-400/30 transition-colors duration-200">
                  <FaArrowLeft className="w-4 h-4" />
                </div>
                <span className="font-medium">Back to Dashboard</span>
              </Link>
            </div>

            {/* Friend Requests Badge */}
            {friendRequests.length > 0 && (
              <button
                onClick={() => setShowRequests(!showRequests)}
                className="relative flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 rounded-xl text-black font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <FaUserPlus className="w-4 h-4" />
                <span>Friend Requests</span>
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold shadow-lg">
                  {friendRequests.length}
                </span>
              </button>
            )}
          </div>

          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-200 bg-clip-text text-transparent">
                Language Learners
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Connect with fellow language enthusiasts and practice together
            </p>
          </div>

          {/* Enhanced Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className={`relative transition-all duration-300 ${searchFocused ? 'transform scale-105' : ''}`}>
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaSearch
                  className={`w-5 h-5 transition-colors duration-200 ${searchFocused ? 'text-yellow-400' : 'text-gray-400'}`}
                />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Search by name or email..."
                className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200 text-lg"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-4 flex items-center">
                  <FaTimes className="w-4 h-4 text-gray-400 hover:text-white transition-colors duration-200" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Friend Requests Panel */}
      {showRequests && friendRequests.length > 0 && (
        <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-b border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                  <FaUserPlus className="w-4 h-4 text-black" />
                </div>
                Pending Friend Requests
              </h2>
              <button onClick={() => setShowRequests(false)} className="p-2 rounded-full hover:bg-yellow-200 transition-colors duration-200">
                <FaTimes className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {friendRequests.map((request) => (
                <div key={request.id} className="bg-white rounded-xl shadow-sm border border-yellow-200 p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 border-2 border-yellow-400">
                      {request.requester_profile_image ? (
                        <img src={request.requester_profile_image} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-500 text-black font-bold text-lg">
                          {request.requester_email.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {request.requester_full_name || request.requester_email}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{request.requester_email}</p>
                      <p className="text-xs text-gray-400">Sent {formatDate(request.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => respondToFriendRequest(request.id, 'accept')}
                      disabled={processingRequests.has(request.id)}
                      className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 rounded-lg text-white transition-colors duration-200 font-medium"
                    >
                      <FaCheck className="w-3 h-3" />
                      <span>Accept</span>
                    </button>
                    <button
                      onClick={() => respondToFriendRequest(request.id, 'decline')}
                      disabled={processingRequests.has(request.id)}
                      className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 rounded-lg text-white transition-colors duration-200 font-medium"
                    >
                      <FaTimes className="w-3 h-3" />
                      <span>Decline</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 font-medium">Your Friends</p>
                <p className="text-3xl font-bold text-gray-900">{acceptedCount}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <FaUserFriends className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 font-medium">Pending Requests</p>
                <p className="text-3xl font-bold text-gray-900">{friendRequests.length}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <FaClock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 font-medium">Featured Members</p>
                <p className="text-3xl font-bold text-gray-900">{featuredCount}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <FaUsers className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Users Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array(6).fill(0).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 animate-pulse">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
                <div className="h-10 bg-gray-200 rounded-xl"></div>
              </div>
            ))}
          </div>
        ) : displayedUsers.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {displayedUsers.map((user) => (
                <div
                  key={user.id}
                  className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group relative"
                >
                  <div className="p-8 text-center">
                    {/* Featured Badge */}
                    {user.is_featured && (
                      <div className="mb-4">
                        <span className="inline-block px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-full text-sm font-bold shadow-lg">
                          Featured
                        </span>
                      </div>
                    )}

                    <div className="mb-6">
                      <div className="relative">
                        <div className="w-24 h-24 mx-auto rounded-full overflow-hidden bg-gray-200 border-4 border-yellow-400 shadow-lg">
                          {user.profile_image ? (
                            <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-500 text-black font-bold text-2xl">
                              {user.email.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div
                          className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-3 border-white shadow-lg ${
                            user.is_online ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                        ></div>
                      </div>
                    </div>

                    {/* User Name */}
                    <div className="mb-6">
                      {user.username || user.full_name ? (
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-yellow-600 transition-colors duration-200">
                          {user.username || user.full_name}
                        </h3>
                      ) : (
                        <div className="mb-8"></div>
                      )}
                      
                      <div className="flex items-center justify-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${user.is_online ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                        <p className="text-sm text-gray-500">
                          {user.is_online ? 'Online' : 'Offline'}
                        </p>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div>{getFriendshipButton(user)}</div>
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </div>
              ))}
            </div>

            {users.length > INITIAL_DISPLAY_COUNT && (
              <div className="text-center">
                {!showMore ? (
                  <button
                    onClick={handleSeeMore}
                    className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-black text-white rounded-2xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
                  >
                    <FaUsers className="w-5 h-5" />
                    <span>See More Learners</span>
                  </button>
                ) : (
                  <button
                    onClick={handleShowLess}
                    className="inline-flex items-center space-x-2 px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
                  >
                    <FaTimes className="w-5 h-5" />
                    <span>Show Less</span>
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaUsers className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No learners found</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchQuery
                ? `No users match "${searchQuery}". Try a different search term.`
                : 'No users available at the moment. Check back later!'}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black rounded-xl font-medium transition-colors duration-200"
              >
                <FaTimes className="w-4 h-4" />
                <span>Clear Search</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Floating Action Button for Mobile */}
      <div className="fixed bottom-6 right-6 md:hidden">
        <Link
          to="/friends"
          className="w-14 h-14 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110"
        >
          <FaUserFriends className="w-6 h-6 text-black" />
        </Link>
      </div>
    </div>
  ); 
}

/** Friendly error text for common Supabase/RLS issues */
function humanizeError(err: any, fallback: string) {
  const msg = String(err?.message || '');
  if (msg.toLowerCase().includes('row level security') || err?.code === 'PGRST301') {
    return 'Not authorized by RLS. Ensure a valid Supabase auth session and policies allow these selects.';
  }
  if (err?.code === 'PGRST116') return fallback; // no rows
  return fallback;
}
